import React, { useState } from 'react';
import {
  SiteBlackout,
  SiteBlackoutCreate,
  SiteBlackoutUpdate,
  BlackoutType,
  BLACKOUT_TYPE_LABELS
} from '../../../types/site';
import { siteService } from '../../../services/siteService';
import {
  isBlackoutCurrentlyActive,
  formatBlackoutDate,
  getBlackoutTypeIcon,
  validateBlackoutDraft,
} from '../../../utils/blackoutUtils';

interface SiteBlackoutsFormProps {
  siteId: number;
  storeId: number;
  blackouts: SiteBlackout[];
  onChange: (blackouts: SiteBlackout[]) => void;
}

const SiteBlackoutsForm: React.FC<SiteBlackoutsFormProps> = ({
  siteId,
  storeId,
  blackouts,
  onChange
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<{
    blackout_type: BlackoutType;
    title: string;
    start_date: string;
    end_date: string;
    description: string;
    active: boolean;
  }>({
    blackout_type: 'maintenance',
    title: '',
    start_date: '',
    end_date: '',
    description: '',
    active: true
  });

  const resetForm = () => {
    setFormData({
      blackout_type: 'maintenance',
      title: '',
      start_date: '',
      end_date: '',
      description: '',
      active: true
    });
    setShowForm(false);
    setEditingId(null);
    setError(null);
  };

  const handleEdit = (blackout: SiteBlackout) => {
    setFormData({
      blackout_type: blackout.blackout_type,
      title: blackout.title,
      start_date: blackout.start_date,
      end_date: blackout.end_date,
      description: blackout.description || '',
      active: blackout.active
    });
    setEditingId(blackout.id);
    setShowForm(true);
    setError(null);
  };

  const handleSave = async () => {
    const validationError = validateBlackoutDraft(formData);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingId !== null) {
        const updateData: SiteBlackoutUpdate = {
          blackout_type: formData.blackout_type,
          title: formData.title.trim(),
          // Sent even when empty so clearing the description persists.
          description: formData.description.trim(),
          start_date: formData.start_date,
          end_date: formData.end_date,
          active: formData.active
        };

        const updated = await siteService.updateSiteBlackout(editingId, updateData);
        onChange(blackouts.map(b => (b.id === editingId ? updated : b)));
      } else {
        const createData: SiteBlackoutCreate = {
          site_id: siteId,
          store_id: storeId,
          blackout_type: formData.blackout_type,
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          start_date: formData.start_date,
          end_date: formData.end_date,
          active: formData.active
        };

        const created = await siteService.createSiteBlackout(createData);
        onChange([...blackouts, created]);
      }

      resetForm();
    } catch (err) {
      setError(editingId !== null ? 'Failed to update blackout period' : 'Failed to create blackout period');
      console.error('Error saving blackout:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (blackout: SiteBlackout) => {
    try {
      const updated = await siteService.updateSiteBlackout(blackout.id, { active: !blackout.active });
      onChange(blackouts.map(b => b.id === blackout.id ? updated : b));
    } catch (err) {
      console.error('Error updating blackout:', err);
    }
  };

  const handleDelete = async (blackoutId: number) => {
    if (!window.confirm('Delete this blackout period?')) return;

    try {
      await siteService.deleteSiteBlackout(blackoutId);
      onChange(blackouts.filter(b => b.id !== blackoutId));
    } catch (err) {
      console.error('Error deleting blackout:', err);
    }
  };

  // Sort blackouts: active first, then by start date
  const sortedBlackouts = [...blackouts].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return a.start_date.localeCompare(b.start_date); // YYYY-MM-DD sorts lexically
  });

  return (
    <div className="site-blackouts-form">
      <p className="form-hint">
        Set periods when this site is unavailable for booking (maintenance, events, etc.)
      </p>

      {/* Existing Blackouts */}
      {sortedBlackouts.length > 0 && (
        <div className="blackouts-list">
          {sortedBlackouts.map(blackout => (
            <div
              key={blackout.id}
              className={`blackout-item ${isBlackoutCurrentlyActive(blackout) ? 'currently-active' : ''} ${!blackout.active ? 'inactive' : ''}`}
            >
              <div className="blackout-item-icon">
                {getBlackoutTypeIcon(blackout.blackout_type)}
              </div>
              <div className="blackout-item-info">
                <strong>
                  {BLACKOUT_TYPE_LABELS[blackout.blackout_type]}
                  {isBlackoutCurrentlyActive(blackout) && <span className="active-badge">Active Now</span>}
                </strong>
                <span className="blackout-dates">
                  {formatBlackoutDate(blackout.start_date)} — {formatBlackoutDate(blackout.end_date)}
                </span>
                {blackout.description && (
                  <span className="blackout-reason">{blackout.description}</span>
                )}
              </div>
              <div className="blackout-item-actions">
                <button
                  className="btn btn-text btn-sm"
                  onClick={() => handleEdit(blackout)}
                  disabled={saving}
                >
                  Edit
                </button>
                <button
                  className="btn btn-text btn-sm"
                  onClick={() => handleToggleActive(blackout)}
                >
                  {blackout.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  className="btn btn-text btn-sm btn-danger"
                  onClick={() => handleDelete(blackout.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {blackouts.length === 0 && !showForm && (
        <div className="config-empty">
          <p>No blackout periods configured for this site.</p>
        </div>
      )}

      {/* Add Blackout Form */}
      {showForm ? (
        <div className="blackout-form-card">
          <h4>{editingId !== null ? 'Edit Blackout Period' : 'Add Blackout Period'}</h4>

          {error && <div className="form-error">{error}</div>}

          <div className="form-row">
            <div className="form-group">
              <label>Blackout Type</label>
              <select
                value={formData.blackout_type}
                onChange={(e) => setFormData({ ...formData, blackout_type: e.target.value as BlackoutType })}
              >
                {Object.entries(BLACKOUT_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {getBlackoutTypeIcon(key as BlackoutType)} {label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Start Date <span className="required">*</span></label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End Date <span className="required">*</span></label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Title <span className="required">*</span></label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Scheduled Maintenance, Private Event"
            />
          </div>

          <div className="form-group">
            <label>Description (optional)</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="e.g., Plumbing repair on site 12"
            />
          </div>

          <div className="toggle-row">
            <div className="toggle-label">
              <span>Active</span>
              <span>Enable this blackout period</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="form-actions">
            <button className="btn btn-secondary" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving
                ? (editingId !== null ? 'Saving...' : 'Adding...')
                : (editingId !== null ? 'Save Changes' : 'Add Blackout')}
            </button>
          </div>
        </div>
      ) : (
        <div className="config-card-footer">
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
            + Add Blackout Period
          </button>
        </div>
      )}
    </div>
  );
};

export default SiteBlackoutsForm;
